/**
 * ResidentRewardBadge — Iter-99 Silver / Gold / Platinum status pill.
 *
 * Sourced from Operations-level compute at:
 *   GET /api/tenant/reward-status/me      (self, on the resident dashboard)
 *   GET /api/rewards/user/{user_id}       (PM / CS / Ops read-only lookup)
 *
 * Read-only in every context — the badge itself never mutates state.
 *
 * Colors:
 *   Silver   → grey
 *   Gold     → gold
 *   Platinum → blue-purple gradient
 *   None / not yet eligible → subtle "—" chip (only rendered when size='full')
 */
import { useEffect, useState } from 'react';
import { apiSafeFetch } from '../lib/apiFetch';

const TIER_STYLE = {
  silver: {
    bg: '#8B8B8B',
    fg: '#111',
    label: 'Silver',
    ring: 'rgba(139,139,139,0.35)',
  },
  gold: {
    bg: '#C9A962',
    fg: '#111',
    label: 'Gold',
    ring: 'rgba(201,169,98,0.35)',
  },
  platinum: {
    // Blue → purple gradient per spec.
    bg: 'linear-gradient(135deg, #4C6EF5 0%, #7048E8 60%, #9775FA 100%)',
    fg: '#fff',
    label: 'Platinum',
    ring: 'rgba(112,72,232,0.35)',
  },
};

/**
 * `userId` — when set, loads via the PM / CS / Ops endpoint. Otherwise loads
 *            the current signed-in resident's badge from `/reward-status/me`.
 * `size`   — 'compact' (just the star pill · header + resident cards) or
 *            'full' (adds tier label · profile page + resident record row).
 * `showNone` — force-render the "—" chip even when the resident isn't yet
 *            eligible. Off by default (compact) so the header stays clean.
 */
export default function ResidentRewardBadge({
  userId = null,
  size = 'compact',
  showNone = false,
  testIdPrefix = 'resident-reward',
}) {
  const [state, setState] = useState({ loading: true, badge: null, reason: '' });

  useEffect(() => {
    let alive = true;
    // Iter-102 · Item 5 — shared apiFetch helper.
    const url = userId ? `/api/rewards/user/${userId}` : '/api/resident-rewards/me';
    apiSafeFetch(url).then(({ data }) => {
      if (!alive) return;
      if (!data) { setState({ loading: false, badge: null, reason: '' }); return; }
      setState({
        loading: false,
        points: data.balance,
        contractEligible: data.contract_eligible,
        badge: data.badge || null,
        reason: data.reason || '',
        metrics: data.metrics || {},
      });
    });
    return () => { alive = false; };
  }, [userId]);

  if (state.loading) return null;
  if (!userId && state.contractEligible === false) return null;
  if (!userId && state.points != null) return <a href="/resident-rewards" className="text-xs font-bold text-[#C9A962]" data-testid={`${testIdPrefix}-points`}>★ {state.points.toLocaleString()} points</a>;

  const style = state.badge ? TIER_STYLE[state.badge] : null;

  // Not-yet-eligible chip only when explicitly requested (profile + PM row).
  if (!style) {
    if (!showNone) return null;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
        style={{ backgroundColor: '#2D2D2D', color: '#6b6b6b', border: '1px solid #4a4a4a' }}
        title={state.reason || 'Not yet eligible for a reward tier.'}
        data-testid={`${testIdPrefix}-none`}
      >
        Reward · —
      </span>
    );
  }

  const label = style.label;
  const isPlatinum = state.badge === 'platinum';
  const compact = size === 'compact';

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-bold uppercase tracking-widest transition ${
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
      }`}
      style={{
        background: style.bg,
        color: style.fg,
        boxShadow: `0 0 0 2px ${style.ring}`,
      }}
      title={state.reason}
      data-testid={`${testIdPrefix}-${state.badge}`}
      data-badge={state.badge}
      aria-label={`Reward tier ${label}`}
    >
      {/* Star glyph — subtle differentiator for the platinum tier. */}
      <svg
        width={compact ? 10 : 12}
        height={compact ? 10 : 12}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        style={isPlatinum ? { filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.6))' } : undefined}
      >
        <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18.2 22 12 18.3 5.8 22l1.7-7.2L2 10l7.1-1.1L12 2z" />
      </svg>
      {compact ? label.charAt(0) : label}
    </span>
  );
}
